import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { UserGuideButton } from '@/components/guide/UserGuideButton';
import SiChevronLeft from '@/icons/SiChevronLeft';
import { useFinanceModels, usePeripheralFinanceModels } from '@/services/api/hooks/financemodels';
import { useIsPeripheralModelRequired, useRequiredFinanceModels } from '@/services/catalogue/useFinanceModels';
import { useGroupModelsByCategoryAndManufacturer } from '@/services/catalogue/useGroupFinanceModels';
import { useNavigateAndValidate } from '@/services/catalogue/validation/catalogueHooks';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { FunctionComponent } from 'react';
import { CustomerCatalogueContainer } from '../CustomerCatalogueContainer';
import FinanceModelsSection from './FinanceModelsSection';

const FinanceModelsContent: FunctionComponent = () => {
  const routePrefix = useCustomerRoutePrefix();
  const navigateAndValidate = useNavigateAndValidate();

  const breadcrumbs: NavPage[] = [
    {
      name: 'Select Finance / Purchase Models',
      to: ''
    }
  ];

  const financeModels = useFinanceModels();
  const peripheralModels = usePeripheralFinanceModels();

  const requiredFinanceModels = useRequiredFinanceModels();
  const isPeripheralModelRequired = useIsPeripheralModelRequired();

  const activeGroupedModels = useGroupModelsByCategoryAndManufacturer(requiredFinanceModels);

  const inactiveFinanceModels = financeModels.filter(
    model => !requiredFinanceModels.some(requiredModel => requiredModel.manufacturerId === model.manufacturerId)
  );

  const inActiveGroupedModels = useGroupModelsByCategoryAndManufacturer(inactiveFinanceModels);

  if (financeModels.length === 0) {
    throw Error('Finance models should be defined');
  }

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
      <CataloguePageHeader
        title="Standardise Products"
        subtitle={'Select Finance / Purchase Models'}
        breadcrumbs={breadcrumbs}
      >
        <div className="flex gap-4">
          <UserGuideButton newRoute={`${routePrefix}/advanced/financeModelGuide`} />
          <Button
            onClick={() => {
              navigateAndValidate(`${routePrefix}/catalogue`);
            }}
            variant={ButtonVariant.Mono}
          >
            <SiChevronLeft className="text-xl" />
            <span>Back to Catalogue</span>
          </Button>
        </div>
      </CataloguePageHeader>
      <div className="px-6 py-8 space-y-4 overflow-y-auto h-full">
        <p className="text-2xl font-bold">Please select a finance / purchase model for each active item</p>

        <FinanceModelsSection
          group={activeGroupedModels}
          peripheralModels={isPeripheralModelRequired ? peripheralModels : undefined}
          active
        />
        <FinanceModelsSection
          infoText="These are items which have not been selected to be used in any bundles"
          group={inActiveGroupedModels}
          peripheralModels={isPeripheralModelRequired ? undefined : peripheralModels}
          defaultExpanded={false}
        />
      </div>
    </div>
  );
};

export const FinanceModels: FunctionComponent = () => {
  return (
    <CustomerCatalogueContainer>
      <FinanceModelsContent />
    </CustomerCatalogueContainer>
  );
};
